// =============================================================================
// Phase 1 SOC / Wazuh Integration: Discovery List Component
// =============================================================================

import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useSecurityStore } from '@/stores/securityStore';
import { supabase } from '@/integrations/supabase/client';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Link2, 
  Plus, 
  EyeOff, 
  Search, 
  Wifi, 
  WifiOff,
  Server,
  AlertCircle,
} from 'lucide-react';
import { DiscoveryLinkDialog } from './DiscoveryLinkDialog';
import { CreateEndpointFromDiscoveryDialog } from './CreateEndpointFromDiscoveryDialog';
import type { DiscoveredIdentity, DiscoveryStatus } from '@/types/security';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';

interface Site {
  id: string;
  name: string;
}

export function DiscoveryList() {
  const { profile } = useAuth();
  const orgId = profile?.org_id;
  
  const { 
    discoveries, 
    discoveriesLoading, 
    discoveriesError,
    fetchDiscoveries,
    ignoreDiscovery,
    wazuhConfig,
  } = useSecurityStore();
  
  const [sites, setSites] = useState<Site[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<DiscoveryStatus | 'all'>('all');
  const [selectedSite, setSelectedSite] = useState<string>('all');
  const [selectedConnected, setSelectedConnected] = useState<'all' | 'online' | 'offline'>('all');
  
  // Dialog state
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedDiscovery, setSelectedDiscovery] = useState<DiscoveredIdentity | null>(null);
  
  // Fetch sites on mount
  useEffect(() => {
    if (orgId) {
      supabase
        .from('sites')
        .select('id, name')
        .eq('org_id', orgId)
        .then(({ data }) => setSites(data || []));
    }
  }, [orgId]);
  
  // Apply filters when they change
  useEffect(() => {
    if (!orgId) return;
    
    const newFilters = {
      site_id: selectedSite !== 'all' ? selectedSite : undefined,
      status: selectedStatus !== 'all' ? selectedStatus : undefined,
      connected: selectedConnected === 'all' ? undefined : selectedConnected === 'online',
      search: searchQuery || undefined,
    };
    
    fetchDiscoveries(orgId, newFilters);
  }, [orgId, selectedSite, selectedStatus, selectedConnected, searchQuery, fetchDiscoveries]);
  
  // Handle link action
  const handleLink = (discovery: DiscoveredIdentity) => {
    setSelectedDiscovery(discovery);
    setLinkDialogOpen(true);
  };
  
  // Handle create endpoint action
  const handleCreateEndpoint = (discovery: DiscoveredIdentity) => {
    setSelectedDiscovery(discovery);
    setCreateDialogOpen(true);
  };
  
  // Handle ignore action
  const handleIgnore = async (discovery: DiscoveredIdentity) => {
    try {
      await ignoreDiscovery(discovery.id);
      toast.success('Discovery marked as ignored');
    } catch {
      toast.error('Failed to ignore discovery');
    }
  };
  
  // Render connection status badge
  const renderConnectionStatus = (discovery: DiscoveredIdentity) => {
    if (discovery.connected) {
      return (
        <Badge variant="default" className="bg-green-500/20 text-green-600 border-green-500/30">
          <Wifi className="h-3 w-3 mr-1" />
          Online
        </Badge>
      );
    }
    return (
      <Badge variant="secondary" className="bg-muted text-muted-foreground">
        <WifiOff className="h-3 w-3 mr-1" />
        Offline
      </Badge>
    );
  };
  
  // Render status badge
  const renderStatusBadge = (status: DiscoveryStatus) => {
    switch (status) {
      case 'linked':
        return <Badge variant="default">Linked</Badge>;
      case 'ignored':
        return <Badge variant="secondary">Ignored</Badge>;
      default:
        return <Badge variant="outline">Unlinked</Badge>;
    }
  };
  
  // Show configuration prompt if no Wazuh config
  if (!wazuhConfig && !discoveriesLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-amber-500" />
            Wazuh Not Configured
          </CardTitle>
          <CardDescription>
            Configure your Wazuh connection in the Configuration tab to start discovering network identities.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }
  
  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Discovered Network Identities</CardTitle>
          <CardDescription>
            Network devices discovered via Wazuh. Link them to existing endpoints or create new ones.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search IP, MAC, or hostname..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={selectedSite} onValueChange={setSelectedSite}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Sites" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sites</SelectItem>
                {sites.map((site) => (
                  <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedStatus} onValueChange={(v) => setSelectedStatus(v as DiscoveryStatus | 'all')}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="unlinked">Unlinked</SelectItem>
                <SelectItem value="linked">Linked</SelectItem>
                <SelectItem value="ignored">Ignored</SelectItem>
              </SelectContent>
            </Select>
            <Select value={selectedConnected} onValueChange={(v) => setSelectedConnected(v as 'all' | 'online' | 'offline')}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="online">Online</SelectItem>
                <SelectItem value="offline">Offline</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
      
      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {discoveriesError ? (
            <div className="p-8 text-center text-danger">Error: {discoveriesError}</div>
          ) : discoveriesLoading ? (
            <div className="p-8 text-center text-muted-foreground">Loading discoveries...</div>
          ) : discoveries.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Server className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No discoveries found. Click "Sync Now" to fetch from Wazuh.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Connection</TableHead>
                  <TableHead>IP Address</TableHead>
                  <TableHead>MAC Address</TableHead>
                  <TableHead>Hostname</TableHead>
                  <TableHead>Site</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Seen</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {discoveries.map((discovery) => (
                  <TableRow key={discovery.id}>
                    <TableCell>{renderConnectionStatus(discovery)}</TableCell>
                    <TableCell className="font-mono text-sm">{discovery.ip || '-'}</TableCell>
                    <TableCell className="font-mono text-sm">{discovery.mac || '-'}</TableCell>
                    <TableCell>{discovery.hostname || '-'}</TableCell>
                    <TableCell>{discovery.site?.name || <span className="text-muted-foreground">Unassigned</span>}</TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        {renderStatusBadge(discovery.status)}
                        {discovery.endpoint && <span className="text-xs text-muted-foreground">→ {discovery.endpoint.name}</span>}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDistanceToNow(new Date(discovery.last_seen_at), { addSuffix: true })}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        {discovery.status === 'unlinked' && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => handleLink(discovery)} title="Link to existing endpoint">
                              <Link2 className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleCreateEndpoint(discovery)} title="Create new endpoint">
                              <Plus className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleIgnore(discovery)} title="Ignore">
                              <EyeOff className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      
      {selectedDiscovery && (
        <>
          <DiscoveryLinkDialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen} discovery={selectedDiscovery} />
          <CreateEndpointFromDiscoveryDialog open={createDialogOpen} onOpenChange={setCreateDialogOpen} discovery={selectedDiscovery} />
        </>
      )}
    </div>
  );
}
